/**
 * DISBURSEMENT REPORT COMPONENT
 * Shows what was disbursed per quarter per facility
 */

import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ReportTableComponent } from '../shared/components/report-table.component';
import { ReportService } from '../shared/services/report.service';
import { ReportPivotService } from '../shared/services/report-pivot.service';
import {
  DisbursementReportRow,
  ExposureReportRow,
  ReportDateRange,
} from '../shared/models/report.model';

@Component({
  selector: 'app-disbursement-report',
  standalone: true,
  imports: [CommonModule, ReportTableComponent, Popup, TranslatePipe],
  template: `
    <div class="flex flex-col gap-6 p-6">
      <!-- Header Section -->
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-lg sm:text-xl font-semibold text-[#444A6D]">{{ 'REPORTS.DISBURSEMENT.TITLE' | translate }}</h1>
          <p class="text-gray-600 mt-1 text-sm">{{ 'REPORTS.DISBURSEMENT.SUBTITLE' | translate }}</p>
        </div>
        <button
          (click)="openFilterModal()"
          class="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
        >
          🔍 {{ 'REPORTS.GENERATE_REPORT' | translate }}
        </button>
      </div>

      <!-- Filter Modal -->
      <app-popup
        [show]="showFilterModal"
        title="REPORTS.SELECT_DATE_RANGE"
        (showChange)="showFilterModal = $event"
        [fields]="filterFields"
        [data]="filterData"
        (formChange)="onFilterChange($event)"
        [onSave]="onGenerateReport"
      ></app-popup>

      <!-- Loading State -->
      @if (isLoading) {
        <div class="flex justify-center items-center h-64">
          <div class="text-center">
            <div
              class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
            ></div>
            <p class="mt-4 text-gray-600">{{ 'REPORTS.LOADING_REPORT' | translate }}</p>
          </div>
        </div>
      }

      <!-- Error State -->
      @if (errorMessage) {
        <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          ❌ {{ errorMessage | translate }}
          <button (click)="clearError()" class="ms-4 underline font-semibold">{{ 'REPORTS.DISMISS' | translate }}</button>
        </div>
      }

      <!-- Report Table -->
      @if (reportRows.length > 0 && !isLoading) {
        <app-report-table
          [title]="'Disbursement Report - ' + formatDateRange()"
          [displayTitle]="'REPORTS.DISBURSEMENT.TITLE_RANGE' | translate: dateRangeParams()"
          [rows]="reportRows"
          [quarterColumns]="quarterColumns"
          [startDate]="currentDateRange ? currentDateRange.startDate : null"
          [endDate]="currentDateRange ? currentDateRange.endDate : null"
          (exportCSV)="onExportComplete()"
        ></app-report-table>
      }

      <!-- Empty State -->
      @if (reportRows.length === 0 && !isLoading && !errorMessage) {
        <div class="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
          <span class="text-6xl">📊</span>
          <p class="mt-4 text-xl text-gray-600">{{ 'REPORTS.NO_DATA_TO_DISPLAY' | translate }}</p>
          <p class="text-gray-500">{{ 'REPORTS.EMPTY_HINT_VIEW' | translate }}</p>
        </div>
      }
    </div>
  `,
})
export class DisbursementReportComponent implements OnInit {
  private reportService = inject(ReportService);
  private pivotService = inject(ReportPivotService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  showFilterModal = false;
  isLoading = false;
  errorMessage = '';

  reportRows: (DisbursementReportRow | ExposureReportRow)[] = [];
  quarterColumns: string[] = [];

  currentDateRange: ReportDateRange | null = null;

  // Filter form configuration
  filterFields: DynamicFieldConfig[] = [
    {
      key: 'startDate',
      label: 'REPORTS.START_DATE',
      type: 'date',
      validations: { required: true },
      gridClass: 'col-span-1',
    },
    {
      key: 'endDate',
      label: 'REPORTS.END_DATE',
      type: 'date',
      validations: { required: true },
      gridClass: 'col-span-1',
    },
  ];

  filterData = {
    startDate: this.getDefaultStartDate(),
    endDate: new Date().toISOString().split('T')[0],
  };

  ngOnInit(): void {
    // Initialize with default date range
    this.generateReport(this.filterData);
  }

  /**
   * Open filter modal
   */
  openFilterModal(): void {
    this.showFilterModal = true;
  }

  /**
   * Handle filter form changes
   */
  onFilterChange(data: any): void {
    this.filterData = data;
  }

  /**
   * Generate report - returns a function for the popup component
   */
  onGenerateReport = (data: any): void => {
    this.generateReport(data);
  };

  /**
   * Main report generation logic
   */
  private generateReport(filterData: any): void {
    // Validate dates
    if (!filterData.startDate || !filterData.endDate) {
      this.errorMessage = 'REPORTS.SELECT_BOTH_DATES_ERROR';
      this.toastr.error(this.translate.instant('REPORTS.SELECT_BOTH_DATES'));
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.reportRows = [];
    this.quarterColumns = [];

    const dateRange: ReportDateRange = {
      startDate: filterData.startDate,
      endDate: filterData.endDate,
    };

    this.currentDateRange = dateRange;

    // Call API
    this.reportService.getDisbursementReport(dateRange).subscribe({
      next: (rawData) => {
        // Transform raw data into pivoted rows
        // Use 'dueAmt' for quarterly breakdown
        this.reportRows = this.pivotService.transformDisbursementReport(rawData, 'dueAmt');
        this.quarterColumns = this.pivotService.getQuarterColumns(rawData);

        this.isLoading = false;

        if (this.reportRows.length === 0) {
          this.toastr.info(this.translate.instant('REPORTS.NO_DATA_FOR_RANGE'));
        } else {
          this.toastr.success(
            this.translate.instant('REPORTS.REPORT_GENERATED', { count: this.reportRows.length }),
          );
        }

        // Close modal after successful generation
        this.showFilterModal = false;
      },
      error: (error) => {
        this.isLoading = false;
        const errMsg =
          error?.error?.message || error?.message || 'REPORTS.GENERATE_FAILED';
        this.errorMessage = errMsg;
        this.toastr.error(this.translate.instant(errMsg));
      },
    });
  }

  /**
   * Format date range for display
   */
  formatDateRange(): string {
    if (!this.currentDateRange) return '';
    const start = new Date(this.currentDateRange.startDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const end = new Date(this.currentDateRange.endDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    return `${start} to ${end}`;
  }

  /**
   * Date range split into params for the translated (display-only) title
   */
  dateRangeParams(): { start: string; end: string } {
    const [start = '', end = ''] = this.formatDateRange().split(' to ');
    return { start, end };
  }

  /**
   * Clear error message
   */
  clearError(): void {
    this.errorMessage = '';
  }

  /**
   * Export completion callback
   */
  onExportComplete(): void {
    this.toastr.success(this.translate.instant('REPORTS.EXPORT_SUCCESS'));
  }

  /**
   * Get default start date (3 months ago)
   */
  private getDefaultStartDate(): string {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.toISOString().split('T')[0];
  }
}
